import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useRef } from 'react';
import { useThemeColors } from '../../providers/ThemeProvider';
import { cn } from '../../utils/cn';

export interface TabOption {
	id: string;
	label: string;
	icon?: IconDefinition;
	disabled?: boolean;
}

interface TabsProps {
	tabs: TabOption[];
	activeTab: string;
	onTabChange: (tabId: string) => void;
	variant?: 'default' | 'pills' | 'underline';
	alignment?: 'left' | 'center' | 'right';
	showIcons?: boolean;
	className?: string;
}

export const Tabs: React.FC<TabsProps> = ({
	tabs,
	activeTab,
	onTabChange,
	variant = 'default',
	alignment = 'left',
	showIcons = false,
	className,
}) => {
	const colors = useThemeColors();
	const listRef = useRef<HTMLDivElement | null>(null);

	// At phone width a six-tab strip is wider than the screen. The strip
	// scrolls sideways instead of overflowing the page, and the active tab is
	// kept in view so a deep link to the last tab does not open on a strip
	// that looks like it ends at the third (PD-15939).
	useEffect(() => {
		const list = listRef.current;
		if (list == null) {
			return;
		}
		const active = list.querySelector<HTMLElement>('[aria-selected="true"]');
		if (active != null && list.scrollWidth > list.clientWidth) {
			active.scrollIntoView({ block: 'nearest', inline: 'nearest' });
		}
	}, [activeTab]);

	const alignmentClasses = {
		left: 'justify-start',
		center: 'justify-center',
		right: 'justify-end',
	};

	const getTabClasses = (tab: TabOption, isActive: boolean) => {
		// PD-16082 — the ring only for keyboard focus. `focus:` painted a box
		// after every mouse click, clipped to three sides by the scrolling
		// tablist, until the next click elsewhere; the active underline is the
		// selection cue, and `focus-visible` keeps the ring for Tab navigation.
		const baseClasses = cn(
			'flex-none whitespace-nowrap px-4 py-2 font-medium text-sm transition-colors duration-150',
			'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-inset',
			tab.disabled && 'opacity-50 cursor-not-allowed',
		);

		switch (variant) {
			case 'pills':
				return cn(
					baseClasses,
					'rounded-md',
					isActive
						? cn(colors.classes.bgPrimary, colors.classes.textWhite)
						: cn(
								colors.classes.textPrimary,
								'hover:' + colors.classes.bgSecondary,
							),
				);
			case 'underline':
				return cn(
					baseClasses,
					'border-b-2',
					isActive
						? cn('border-accent', colors.classes.textPrimary)
						: cn(
								'border-transparent',
								colors.classes.textPrimary,
								'hover:opacity-80 hover:border-strong',
							),
				);
			default:
				return cn(
					baseClasses,
					'border-b-2',
					isActive
						? cn('border-accent', colors.classes.textPrimary)
						: cn(
								'border-transparent',
								colors.classes.textSecondary,
								'hover:' + colors.classes.textPrimary,
							),
				);
		}
	};

	return (
		<div className={cn('w-full', className)}>
			<div
				ref={listRef}
				role="tablist"
				className={cn(
					'flex overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
					alignmentClasses[alignment],
					variant === 'underline' &&
						cn('border-b', colors.classes.borderDefault),
				)}
			>
				{tabs.map((tab) => (
					<button
						key={tab.id}
						type="button"
						// role="tab" + aria-selected aligns with the WAI-ARIA tab
						// pattern. Without these, screen readers announce the tab
						// strip as a generic group of buttons, and tests using
						// `getByRole('tab', ...)` (the conventional accessible
						// selector) can't find tabs at all.
						role="tab"
						aria-selected={activeTab === tab.id}
						aria-disabled={tab.disabled === true ? true : undefined}
						onClick={() => !tab.disabled && onTabChange(tab.id)}
						className={getTabClasses(tab, activeTab === tab.id)}
						disabled={tab.disabled}
					>
						{/* PD-14355: plain spans so the label inherits the button's
						    text-sm. The previous nested <Text> defaulted to text-base,
						    overriding the intended size and making every tab label
						    across the app look oversized/inconsistent. */}
						<span className="flex items-center space-x-2">
							{showIcons && tab.icon && (
								<FontAwesomeIcon icon={tab.icon} className="w-4 h-4" />
							)}
							<span>{tab.label}</span>
						</span>
					</button>
				))}
			</div>
		</div>
	);
};
